/**
 * Read-only CodeBlock extension with HTML embed support for the public docs site.
 *
 * For code blocks with language="html-embed", renders a sandboxed iframe preview
 * with an optional fullscreen overlay (read-only, mirrors the editor experience).
 * For all other languages, renders a standard styled <pre><code> block.
 */

import React from "react";
import { createPortal } from "react-dom";
import { FileCode2, Maximize2, Minimize2, ExternalLink } from "lucide-react";
import CodeBlock from "@tiptap/extension-code-block";
import {
  NodeViewWrapper,
  NodeViewContent,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react";

import { embedSandbox, isSafeEmbedSrc } from "./transform-html-embeds";

const HTML_EMBED_LANGUAGE = "html-embed";

/** Parse the content of an html-embed code block into src/html/height/label. */
function parseHtmlEmbedDirective(content: string): {
  src?: string;
  html?: string;
  height?: number;
  label?: string;
} {
  const lines = content.split("\n");
  let src: string | undefined;
  let height: number | undefined;
  let label: string | undefined;
  let bodyStartIdx = 0;

  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (trimmed === "") {
      bodyStartIdx = i + 1;
      continue;
    }
    const m = trimmed.match(/^(src|height|label)=(.*)$/);
    if (!m) break;
    const value = m[2].trim();
    if (m[1] === "src") src = isSafeEmbedSrc(value) ? value : undefined;
    else if (m[1] === "height") {
      const n = parseInt(value, 10);
      if (!Number.isNaN(n)) height = n;
    } else if (m[1] === "label") label = value || undefined;
    bodyStartIdx = i + 1;
  }

  const body = lines.slice(bodyStartIdx).join("\n").trim();

  if (body && !src) {
    if (!body.includes("\n") && isSafeEmbedSrc(body)) {
      return { src: body, height, label };
    }
    if (body.startsWith("<")) {
      return { src, html: body, height, label };
    }
  }

  return { src, height, label };
}

/** Detect if embed content is a known video (YouTube, Loom). */
const isVideoEmbedUrl = (src?: string, html?: string) => {
  const check = (s: string) => /youtube\.com\/embed\//.test(s) || /loom\.com\/embed\//.test(s);
  if (src && check(src)) return true;
  if (html && check(html)) return true;
  return false;
};

// ----------------------------------------------------------------------------
// HtmlEmbedFullscreen — read-only full-viewport overlay
// ----------------------------------------------------------------------------

interface HtmlEmbedFullscreenProps {
  src?: string;
  html?: string;
  label?: string;
  onClose: () => void;
}

const HtmlEmbedFullscreen = React.memo(
  ({ src, html, label, onClose }: HtmlEmbedFullscreenProps) => {
    /** Append autoplay param for YouTube/Loom when opening fullscreen. */
    const fullscreenSrc = React.useMemo(() => {
      if (!src) return src;
      if (/youtube\.com\/embed\//.test(src)) {
        const sep = src.includes("?") ? "&" : "?";
        return `${src}${sep}autoplay=1`;
      }
      if (/loom\.com\/embed\//.test(src)) {
        const sep = src.includes("?") ? "&" : "?";
        return `${src}${sep}autoplay=1`;
      }
      return src;
    }, [src]);

    React.useEffect(() => {
      function handleEscape(e: KeyboardEvent) {
        if (e.key === "Escape") onClose();
      }
      window.addEventListener("keydown", handleEscape);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = prevOverflow;
      };
    }, [onClose]);

    if (typeof document === "undefined") return null;

    return createPortal(
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[60] flex flex-col bg-background/95 backdrop-blur-sm"
        onClick={onClose}
      >
        <div
          className="flex items-center justify-between gap-3 px-4 h-12 border-b bg-background shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground min-w-0">
            <FileCode2 className="h-4 w-4 shrink-0" />
            <span className="truncate">{label ?? "HTML embed"}</span>
          </span>
          <div className="flex items-center gap-2">
            {src && (
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Open in new tab
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              onClick={onClose}
              aria-label="Exit fullscreen"
              title="Exit fullscreen (Esc)"
              className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          className="flex-1 min-h-0 bg-white overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <iframe
            title={label ?? "HTML embed (fullscreen)"}
            src={fullscreenSrc}
            srcDoc={html}
            sandbox={embedSandbox(fullscreenSrc)}
            className="w-full h-full block bg-white border-0"
          />
        </div>
      </div>,
      document.body,
    );
  },
);

// ----------------------------------------------------------------------------
// HtmlEmbedReadOnly — inline embed with fullscreen toggle
// ----------------------------------------------------------------------------

const HtmlEmbedReadOnly = ({ node }: NodeViewProps) => {
  const [fullscreen, setFullscreen] = React.useState(false);
  const text = node.textContent;
  const parsed = React.useMemo(() => parseHtmlEmbedDirective(text.trim()), [text]);
  const isVideo = isVideoEmbedUrl(parsed.src, parsed.html);
  const height = parsed.height ?? (isVideo ? undefined : 520);
  const hasContent = Boolean(parsed.src || parsed.html);
  const resolvedHtml = React.useMemo(() => {
    if (!parsed.html || !isVideo) return parsed.html;
    return `<!DOCTYPE html><html><head><style>*{margin:0;padding:0;box-sizing:border-box}html,body{width:100%;height:100%;overflow:hidden}iframe{width:100%;height:100%;border:0}</style></head><body>${parsed.html}</body></html>`;
  }, [parsed.html, isVideo]);

  /** Fullscreen button rendered in the header bar or as an overlay on videos. */
  const fullscreenButton = hasContent ? (
    <button
      onClick={() => setFullscreen(true)}
      title="Fullscreen"
      aria-label="Fullscreen"
      className="inline-flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
    >
      <Maximize2 className="h-3 w-3" />
    </button>
  ) : null;

  if (isVideo && hasContent) {
    return (
      <NodeViewWrapper as="div" className="not-prose my-4">
        <div className="group relative rounded-lg overflow-hidden bg-black">
          <iframe
            title={parsed.label ?? "Video"}
            src={parsed.src}
            srcDoc={resolvedHtml}
            sandbox={embedSandbox(parsed.src)}
            style={{ aspectRatio: "16/9" }}
            className="w-full block border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          {/* Floating fullscreen button — visible on hover */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setFullscreen(true)}
              title="Fullscreen"
              aria-label="Fullscreen"
              className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-black/60 text-white hover:bg-black/80 transition-colors backdrop-blur-sm"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        {fullscreen && (
          <HtmlEmbedFullscreen
            src={parsed.src}
            html={resolvedHtml}
            label={parsed.label}
            onClose={() => setFullscreen(false)}
          />
        )}
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="div" className="not-prose my-4">
      <div className="rounded-lg border border-border overflow-hidden bg-background">
        <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/30 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5 truncate">
            <FileCode2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{parsed.label ?? "HTML embed"}</span>
          </span>
          <div className="flex items-center gap-1.5">
            {parsed.src && (
              <a
                href={parsed.src}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {fullscreenButton}
          </div>
        </div>
        {hasContent ? (
          <iframe
            title={parsed.label ?? "HTML embed"}
            src={parsed.src}
            srcDoc={resolvedHtml}
            sandbox={embedSandbox(parsed.src)}
            style={{ height }}
            className="w-full block bg-white border-0"
          />
        ) : (
          <div
            className="flex items-center justify-center text-xs text-muted-foreground bg-muted/20"
            style={{ height: 160 }}
          >
            Empty HTML embed.
          </div>
        )}
      </div>
      {fullscreen && hasContent && (
        <HtmlEmbedFullscreen
          src={parsed.src}
          html={resolvedHtml}
          label={parsed.label}
          onClose={() => setFullscreen(false)}
        />
      )}
    </NodeViewWrapper>
  );
};

const CodeBlockNodeView = (props: NodeViewProps) => {
  const language = props.node.attrs.language;
  if (language === HTML_EMBED_LANGUAGE) {
    return <HtmlEmbedReadOnly {...props} />;
  }
  return (
    <NodeViewWrapper as="div" className="not-prose">
      <pre className="font-mono text-[0.8125rem] p-4 rounded-lg overflow-x-auto border border-foreground/10 bg-muted">
        <NodeViewContent />
      </pre>
    </NodeViewWrapper>
  );
};

export const CodeBlockHtmlEmbed = CodeBlock.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockNodeView);
  },
});
